// The data seam (plan.md §2, item 2).
//
// Every database call in the app goes through this file. Nothing else imports
// supabase-js. Swapping Supabase for raw PostgREST fetch calls against
// self-hosted Postgres means rewriting the bodies here and nothing else — the
// SQL functions being called are plain Postgres and would not change at all.
//
// Note what is NOT here: no table writes. Reports and corroborations are
// created through RPCs because grid snapping, duplicate suppression and the
// corroboration cooldown all have to be server-side to mean anything
// (plan.md §3, §6). The anon key is public, so anything the client could do
// directly, anyone could do directly.

import { createClient } from '@supabase/supabase-js';
import { i18n } from './i18n.js';

// In dev, fall back to the page origin: the vite dev server proxies /rest to
// the gateway, so whatever host the page was loaded from (localhost or a
// tunnel) is also where the API lives. In production VITE_SUPABASE_URL must
// be set explicitly.
const url = import.meta.env.VITE_SUPABASE_URL
  || (import.meta.env.DEV ? window.location.origin : '');
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export const isConfigured = Boolean(url && publishableKey);

export const supabase = isConfigured
  ? createClient(url, publishableKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,   // completes the magic-link return
        flowType: 'pkce',
      },
    })
  : null;

/** Thrown for anything the caller might want to show the user. */
export class DbError extends Error {
  constructor(messageKey, params, cause) {
    super(i18n.t(messageKey, params));
    this.name = 'DbError';
    this.messageKey = messageKey;
    this.params = params;
    this.cause = cause;
  }
}

function unwrap({ data, error }, messageKey) {
  if (error) {
    // Supabase surfaces network failures as a generic message; distinguish
    // them so the UI can offer "retry" rather than "something went wrong".
    const offline = typeof navigator !== 'undefined' && !navigator.onLine;
    throw new DbError(offline ? 'errors.offline' : messageKey, null, error);
  }
  return data;
}

function requireConfig() {
  if (!isConfigured) throw new DbError('errors.noDb');
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * The handful of server-side tunables the UI needs to behave correctly —
 * chiefly the result cap, which drives the "zoom in to see more" state. Read
 * from the server rather than hardcoded, so retuning it with
 * `npm run admin:settings:set` does not silently desync the two.
 */
export async function fetchConfig() {
  requireConfig();
  return unwrap(await supabase.rpc('public_config'), 'errors.loadConfig') ?? {};
}

// ---------------------------------------------------------------------------
// Taxonomy
// ---------------------------------------------------------------------------

/**
 * The whole taxonomy in one request. ~120 rows is a few KB, and having it all
 * client-side is what lets the picker search instantly without a round trip
 * per keystroke (plan.md §4.2).
 */
export async function fetchTaxonomy() {
  requireConfig();
  const collections = unwrap(
    await supabase.from('collections')
      .select('id, slug, name, icon, sort_order, location_precision_m')
      .order('sort_order'),
    'errors.loadTaxonomy');

  const categories = unwrap(
    await supabase.from('categories')
      .select('id, collection_id, slug, name, icon, sort_order, is_other, retired_at')
      .is('retired_at', null)
      .order('sort_order'),
    'errors.loadTaxonomy');

  const byCollection = new Map(collections.map((c) => [c.id, { ...c, categories: [] }]));
  for (const category of categories) {
    byCollection.get(category.collection_id)?.categories.push(category);
  }
  return [...byCollection.values()];
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** The map's read path: exactly the visible rectangle (plan.md §3, §4.1). */
export async function fetchReportsInBounds({ bounds, daysBack = 7, categoryId = null, limit = null }) {
  requireConfig();
  return unwrap(
    await supabase.rpc('reports_in_bounds', {
      p_min_lat: bounds.south, p_min_lng: bounds.west,
      p_max_lat: bounds.north, p_max_lng: bounds.east,
      p_days_back: daysBack,
      p_limit: limit,
      p_category_id: categoryId,
    }),
    'errors.loadReports') ?? [];
}

/** Distance-ranked, for the initial "near me" load before a viewport exists. */
export async function fetchNearbyReports({ lat, lng, radiusM = null, daysBack = 7 }) {
  requireConfig();
  return unwrap(
    await supabase.rpc('nearby_reports', {
      p_lat: lat, p_lng: lng, p_radius_m: radiusM, p_days_back: daysBack,
    }),
    'errors.loadNearby') ?? [];
}

/**
 * The permalink read path. Resolves regardless of expiry, viewport or history
 * window; returns null only when the row is genuinely gone — purged by
 * retention, or never existed (plan.md §4.3, §7).
 */
export async function fetchReport(id) {
  requireConfig();
  const rows = unwrap(await supabase.rpc('get_report', { p_id: id }), 'errors.loadReport');
  return rows?.[0] ?? null;
}

/** Corroboration history: timestamps only, never identities (plan.md §5). */
export async function fetchReportHistory(id, limit = 100) {
  requireConfig();
  return unwrap(
    await supabase.rpc('report_history', { p_report_id: id, p_limit: limit }),
    'errors.loadHistory') ?? [];
}

export async function fetchMyReports(sessionToken, limit = 50) {
  requireConfig();
  return unwrap(
    await supabase.rpc('my_reports', { p_session_token: sessionToken, p_limit: limit }),
    'errors.loadMyReports') ?? [];
}

export async function fetchLeaderboard({ lat, lng, radiusM = null, daysBack = 7 }) {
  requireConfig();
  return unwrap(
    await supabase.rpc('area_leaderboard', {
      p_lat: lat, p_lng: lng, p_radius_m: radiusM, p_days_back: daysBack,
    }),
    'errors.loadLeaderboard') ?? [];
}

// ---------------------------------------------------------------------------
// Writes — RPC only, never .from().insert()
// ---------------------------------------------------------------------------

/**
 * File a report. Returns the function's own status payload rather than
 * throwing on a duplicate, because a duplicate is a normal outcome the UI
 * turns into "this is already reported — confirm it instead" (plan.md OQ1).
 *
 *   { status: 'created',      report_id, lat, lng, expires_at, snapped, precision_m }
 *   { status: 'duplicate',    report_id, corroboration_count, created_at, ... }
 *   { status: 'rate_limited', reason: 'session'|'area', retry_after }
 *   { status: 'error',        reason }
 *
 * The lat/lng that come back are the SNAPPED ones actually stored — the map
 * should move the pin to them, so the user sees what was published.
 */
export async function createReport({ categoryId, lat, lng, sessionToken }) {
  requireConfig();
  return unwrap(
    await supabase.rpc('create_report', {
      p_category_id: categoryId, p_lat: lat, p_lng: lng, p_session_token: sessionToken,
    }),
    'errors.fileReport');
}

/**
 * Corroborate. Also returns status rather than throwing on the cooldown case,
 * so the UI can show a countdown instead of a dead end (plan.md §4.3).
 *
 * Corroboration is gated on an accurate GPS fix within range of the report
 * (corroboration_max_accuracy_m / corroboration_range_m, exposed via
 * public_config).  lat/lng/accuracyM come straight from navigator.geolocation;
 * the caller is responsible for only offering the button when they clear the
 * gate — the server enforces it regardless.
 *
 *   { status: 'ok',       corroboration_count, expires_at, retry_at }
 *   { status: 'cooldown', retry_at, corroboration_count, expires_at }
 *   { status: 'rate_limited', reason: 'session'|'report', retry_after }
 *   { status: 'error',    reason: 'gps_required'|'out_of_range'|... }
 */
export async function corroborate({ reportId, sessionToken, lat, lng, accuracyM }) {
  requireConfig();
  return unwrap(
    await supabase.rpc('log_corroboration', {
      p_report_id: reportId, p_session_token: sessionToken,
      p_lat: lat, p_lng: lng, p_accuracy_m: accuracyM,
    }),
    'errors.confirmReport');
}

/**
 * Record that a picker search found nothing (plan.md OQ4). Fire-and-forget:
 * a failure here must never interrupt someone filing a report, so it swallows
 * its own errors by design.
 */
export function logPickerMiss(query, chosenCategoryId = null) {
  if (!isConfigured || !query?.trim()) return;
  supabase.rpc('log_picker_miss', {
    p_query: query.trim().slice(0, 60),
    p_chosen_category_id: chosenCategoryId,
  }).then(() => {}, () => {});
}
